import React, { useState, useMemo } from 'react';
import { EXERCISE_LIBRARY } from '../../utils/mockData';
import { calculateWorkoutOutput } from '../../utils/engineStub';
import ExerciseCard from './ExerciseCard';
import MetricBadge from './MetricBadge';
import Button from '../shared/Button';
import GlassCard from '../shared/GlassCard';
import Icon from '../shared/Icon';
import './WorkoutBuilder.css';

export default function WorkoutBuilder({ initial, onSave, onAssign }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [scheduledDate] = useState(initial?.scheduledDate || '');
  const [exercises, setExercises] = useState(initial?.exercises || []);
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [csvDragOver, setCsvDragOver] = useState(false);
  const [csvError, setCsvError] = useState(null);

  const { total } = useMemo(() => calculateWorkoutOutput(exercises), [exercises]);

  const filtered = useMemo(() => {
    if (!search.trim()) return EXERCISE_LIBRARY;
    const q = search.toLowerCase();
    return EXERCISE_LIBRARY.filter(
      (ex) =>
        ex.name.toLowerCase().includes(q) ||
        ex.category.toLowerCase().includes(q)
    );
  }, [search]);

  function addExercise(ex) {
    setExercises((prev) => [
      ...prev,
      {
        exerciseId: ex.id,
        sets: 3,
        reps: ex.type === 'cardio' ? 0 : 8,
        weightLbs: ex.defaultWeight || 0,
        durationSec: ex.type === 'cardio' ? 600 : 0,
        type: ex.type,
      },
    ]);
    setSearch('');
    setPickerOpen(false);
  }

  function updateExercise(index, updated) {
    setExercises((prev) => prev.map((e, i) => (i === index ? updated : e)));
  }

  function removeExercise(index) {
    setExercises((prev) => prev.filter((_, i) => i !== index));
  }

  function parseAndImportCSV(text) {
    setCsvError(null);
    const lines = text.trim().split(/\r?\n/);
    const added = [];
    let skipped = 0;
    for (const line of lines) {
      const [rawName, rawSets, rawReps, rawWeight, rawDuration] = line.split(',').map((s) => s.trim());
      if (!rawName) continue;
      const match = EXERCISE_LIBRARY.find(
        (ex) => ex.name.toLowerCase() === rawName.toLowerCase()
      );
      if (!match) { skipped++; continue; }
      added.push({
        exerciseId: match.id,
        sets: parseInt(rawSets, 10) || 3,
        reps: parseInt(rawReps, 10) || (match.type === 'cardio' ? 0 : 8),
        weightLbs: parseFloat(rawWeight) || match.defaultWeight || 0,
        durationSec: parseInt(rawDuration, 10) || (match.type === 'cardio' ? 600 : 0),
        type: match.type,
      });
    }
    if (added.length === 0) {
      setCsvError(skipped > 0 ? `No matching exercises found (${skipped} unrecognized).` : 'CSV appears empty or malformed.');
      return;
    }
    setExercises((prev) => [...prev, ...added]);
    if (skipped > 0) setCsvError(`Imported ${added.length} exercises. ${skipped} unrecognized rows skipped.`);
  }

  function handleCSVDrop(e) {
    e.preventDefault();
    setCsvDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setCsvError('Please drop a .csv file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => parseAndImportCSV(ev.target.result);
    reader.readAsText(file);
  }

  function handleCSVFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseAndImportCSV(ev.target.result);
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleSave() {
    if (!name.trim()) return;
    onSave?.({ name, description, scheduledDate, exercises, workOutputTarget: total });
  }

  return (
    <div className="workout-builder">
      <div className="workout-builder__meta">
        <input
          className="workout-builder__name-input"
          type="text"
          placeholder="Workout name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
        />
        <button
          className="workout-builder__desc-toggle"
          onClick={() => setShowDesc((v) => !v)}
          type="button"
        >
          <Icon name={showDesc ? 'chevron-up' : 'chevron-down'} size={13} />
          {showDesc ? 'Hide description' : 'Add description'}
        </button>
        {showDesc && (
          <textarea
            className="workout-builder__desc-input"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={280}
            autoFocus
          />
        )}
      </div>

      <GlassCard size="sm" className="workout-builder__output-bar">
        <span className="workout-builder__output-label">Estimated Work Output</span>
        <MetricBadge value={total} size="md" />
      </GlassCard>

      <div className="workout-builder__exercises">
        {exercises.length === 0 && (
          <div className="workout-builder__empty">
            Add exercises below to build your workout.
          </div>
        )}
        {exercises.map((entry, i) => (
          <ExerciseCard
            key={`${entry.exerciseId}-${i}`}
            entry={entry}
            index={i}
            onChange={(updated) => updateExercise(i, updated)}
            onRemove={() => removeExercise(i)}
          />
        ))}
      </div>

      <div className="workout-builder__picker-row">
        <Button
          variant="ghost"
          size="md"
          onClick={() => setPickerOpen((prev) => !prev)}
        >
          {pickerOpen ? '− Close Exercise Picker' : '+ Add Exercise'}
        </Button>
        <button
          className="workout-builder__csv-toggle"
          onClick={() => setShowCsvImport((v) => !v)}
          type="button"
          title="Import from CSV"
          aria-label="Import exercises from CSV"
        >
          <Icon name="upload" size={15} />
        </button>
      </div>

      {showCsvImport && (
        <div
          className={`workout-builder__import ${csvDragOver ? 'workout-builder__import--active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setCsvDragOver(true); }}
          onDragLeave={() => setCsvDragOver(false)}
          onDrop={handleCSVDrop}
        >
          <Icon name="upload" size={20} />
          <span>Drop a .csv file here to import exercises</span>
          <label className="workout-builder__import-browse">
            or browse
            <input
              type="file"
              accept=".csv,text/csv"
              className="workout-builder__import-input"
              onChange={handleCSVFile}
            />
          </label>
          <span className="workout-builder__import-hint">Format: exercise_name, sets, reps, weight_lbs, duration_sec</span>
          {csvError && <span className="workout-builder__csv-error">{csvError}</span>}
        </div>
      )}

      {pickerOpen && (
        <GlassCard size="sm" className="workout-builder__picker anim-slide-in-up">
          <input
            className="workout-builder__picker-search"
            type="text"
            placeholder="Search exercises…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="workout-builder__picker-list">
            {filtered.map((ex) => (
              <button
                key={ex.id}
                className="workout-builder__picker-item"
                onClick={() => addExercise(ex)}
                type="button"
              >
                <div className="workout-builder__picker-item-info">
                  <span className="workout-builder__picker-item-name">{ex.name}</span>
                  <span className="workout-builder__picker-item-cat">{ex.category}</span>
                </div>
                <span className="workout-builder__picker-item-type">{ex.type}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="workout-builder__picker-empty">No exercises found.</p>
            )}
          </div>
        </GlassCard>
      )}

      <div className="workout-builder__actions">
        <Button variant="primary" size="lg" onClick={handleSave} disabled={!name.trim() || exercises.length === 0}>
          Save Workout
        </Button>
      </div>
    </div>
  );
}
